import { useQuery } from '@apollo/client';
import { datadogRum } from '@datadog/browser-rum';
import { useLocation, WindowLocation } from '@reach/router';
import { navigate } from 'gatsby';
import { all, and, any, equals, isEmpty, isNil, not, or } from 'ramda';
import { useCallback, useMemo, useState } from 'react';
import { useDeepCompareEffect } from 'react-use';
import { z } from 'zod';

import { addressSchema } from '@ui/routes/Membership/HomeAddressStep/HomeAddressStep.component';

import { doesNotExist, exists } from '@utils/general';

import { useAuth } from '@providers/AuthProvider';

import { IAccountInfoQueryData } from '@folx-types/member';

import { membership, membershipName } from '@graphql/fragments/membership';
import MEMBER_DEMOGRAPHICS_WITH_SUBS from '@graphql/queries/memberDemographicsWithSubs.query.gql';
import ADDRESS_INFO from '@graphql/queries/AddressInfo.query.gql';

import useMemberInfo, { IFragment, SubscriptionInfo } from './useMemberInfo';
import { IAddressInfo, IAddressInfoQueryData } from '@folx-types/address';

const navigateWithReplace = (route: string) => navigate(route, { replace: true });
const navigateWithLocation = (route: string, location: WindowLocation) => {
	navigate(route, {
		replace: true,
		state: {
			redirectTo: `${location?.pathname}${location?.search}`,
		},
	});
};

const routes = {
	signIn: '/app/sign_in',
	membershipStart: '/app/flows/membership',
	membershipAddress: '/app/flows/membership/address',
	membershipInterests: '/app/flows/membership/interest_capture',
	membershipPersonalInfo: '/app/flows/membership/personal_info',
	membershipOrderSummary: '/app/flows/membership/order_summary',
	membershipInsurance: '/app/flows/membership/insurance',
	accountNameEdit: '/app/member/name_edit',
	unpaidMembership: '/app/member/unpaid',
	dashboard: '/app/member',
	reactivateMembership: '/app/member/reactivate_membership',
} as const;

export const useMemberValidation = (formData?: Record<string, string>, formDataLoaded = true) => {
	const fragments: IFragment[] = [{ name: membershipName, node: membership }];

	const { loggedIn, initialized: authInitialized } = useAuth();
	const [loaded, setLoaded] = useState(false);
	const location = useLocation();

	const { loading: accountInfoLoading, data: accountInfo } = useQuery<IAccountInfoQueryData>(
		MEMBER_DEMOGRAPHICS_WITH_SUBS,
		{
			skip: !loggedIn,
		}
	);

	const { loading: memberInfoLoading, memberInfo } = useMemberInfo<SubscriptionInfo>(fragments);
	const { loading: addressLoading, data: addressData } = useQuery<IAddressInfoQueryData>(
		ADDRESS_INFO,
		{
			skip: memberInfoLoading || doesNotExist(memberInfo?.id),
			variables: {
				memberId: memberInfo?.id,
			},
		}
	);

	useDeepCompareEffect(() => {
		if (
			authInitialized &&
			formDataLoaded &&
			!accountInfoLoading &&
			!addressLoading &&
			!memberInfoLoading
		) {
			if (!loggedIn) {
				setLoaded(true);
			}
			if (loggedIn && !isNil(memberInfo)) {
				setLoaded(true);
			}
		}
	}, [
		accountInfoLoading,
		addressLoading,
		authInitialized,
		memberInfoLoading,
		memberInfo,
		loggedIn,
	]);

	const memberMetadata = useMemo(() => {
		const nameFields = [
			accountInfo?.currentMember?.affirmingFirstName,
			accountInfo?.currentMember?.affirmingLastName,
			accountInfo?.currentMember?.legalFirstName,
			accountInfo?.currentMember?.legalLastName,
		];

		const hasDob = exists(accountInfo?.currentMember?.dob);
		const hasNameData = all(exists, nameFields);
		const noLegacySubscriptions = isEmpty(
			accountInfo?.currentMember.memberSubscriptions?.nodes?.filter(
				(sub) => sub.subscriptionType !== 'MEMBERSHIP'
			)
		);

		const hasAddress = validateAddress(addressData?.addressByMemberIdAndType, formData);

		const noFolxSubscription = doesNotExist(memberInfo?.membershipSubscription);

		const isOnboardingComplete = not(
			isEmpty(
				accountInfo?.currentMember.memberSubscriptions?.nodes?.filter(
					(sub) => sub.subscriptionType === 'MEMBERSHIP'
				)
			)
		);

		const membershipSubscriptionStatus = memberInfo?.membershipSubscription?.status;

		return {
			account: {
				hasDob,
				hasNameData,
				hasAddress,
				hasFullDemographic: and(hasDob, hasNameData),
				isLegacyUser: all(equals(true), [noFolxSubscription, noLegacySubscriptions, hasDob]),
				isIncompleteUser: any(equals(false), [hasNameData, hasDob, hasAddress]),
				isOnboardingComplete,
			},
			membership: {
				isMembershipCanceled: equals(membershipSubscriptionStatus, 'CANCELED'),
				isMembershipInactive: not(
					or(
						equals(membershipSubscriptionStatus, 'ACTIVE'),
						equals(membershipSubscriptionStatus, 'PAST_DUE')
					)
				),
				isMembershipActive: equals(membershipSubscriptionStatus, 'ACTIVE'),
				isMembershipPastDue: equals(membershipSubscriptionStatus, 'PAST_DUE'),
				isMembershipUnpaid: equals(membershipSubscriptionStatus, 'UNPAID'),
				isMembershipIncomplete: or(
					equals(membershipSubscriptionStatus, 'INCOMPLETE'),
					equals(membershipSubscriptionStatus, 'INCOMPLETE_EXPIRED')
				),
				noLegacySubscriptions,
			},
		};
	}, [accountInfo, memberInfo]);

	type ValidateMemberOptions = {
		isSecureRoute?: boolean;
		isProductFlow?: boolean;
	};

	const validateMember = useCallback(
		(options: ValidateMemberOptions = { isSecureRoute: true, isProductFlow: false }) => {
			const { isSecureRoute, isProductFlow } = options;
			const { isLegacyUser, hasFullDemographic, hasNameData, hasAddress } = memberMetadata.account;
			const {
				isMembershipInactive,
				isMembershipCanceled,
				isMembershipUnpaid,
				isMembershipIncomplete,
			} = memberMetadata.membership;

			switch (true) {
				case and(isSecureRoute, not(loggedIn)):
					return navigateWithLocation(routes.signIn, location);
				case isMembershipUnpaid:
					return navigateWithReplace(routes.unpaidMembership);
				case all(equals(true), [isLegacyUser, hasFullDemographic, hasAddress]):
					return navigateWithReplace(routes.membershipInsurance);
				case and(isLegacyUser, hasFullDemographic):
					return navigateWithReplace(routes.membershipPersonalInfo);
				case and(isLegacyUser, hasNameData):
					return navigateWithReplace(routes.membershipInterests);
				case and(isMembershipInactive || isLegacyUser, not(hasNameData)):
					return navigateWithReplace(routes.accountNameEdit);
				case isMembershipIncomplete:
					return navigateWithReplace(routes.membershipOrderSummary);
				case and(isMembershipInactive, not(isMembershipCanceled)):
					return navigateWithReplace(routes.membershipStart);
				case and(isProductFlow, isMembershipInactive):
					return navigate(routes.reactivateMembership, {
						state: { source: 'product-flow' },
						replace: true,
					});
				default:
					return Promise.resolve();
			}
		},
		[loggedIn, memberMetadata.account, memberMetadata.membership, location]
	);

	const protectedNavigate = useCallback(
		async (intent = '') => {
			await validateMember();
			navigate(intent);
		},
		[validateMember]
	);

	return {
		protectedNavigate,
		validateMember,
		loaded,
		memberMetadata,
	};
};

const validateAddress = (address: IAddressInfo | undefined, formData?: Record<string, string>) => {
	if (doesNotExist(address) && doesNotExist(formData)) {
		return false;
	}

	const schemaAddressTransform: Partial<z.input<typeof addressSchema>> = {
		shippingAddressLine1: address?.line1 ?? formData?.shippingAddressLine1,
		shippingCity: address?.city ?? formData?.shippingCity,
		shippingState: address?.state ?? formData?.shippingState,
		shippingZip: address?.zip ?? formData?.shippingZip,
		shippingAddressLine2: address?.line2 ?? formData?.shippingAddressLine2,
	};

	try {
		const validatedAddress = addressSchema.parse(schemaAddressTransform);
		return exists(validatedAddress);
	} catch (error) {
		datadogRum.addError(error);
		return false;
	}
};
